import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { focusManager } from '@tanstack/react-query'
import posthog from 'posthog-js'
import './index.css'
import App from './App.tsx'

/**
 * PostHog Analytics Initialization
 *
 * Initialize PostHog before React renders to ensure all events are captured.
 * Uses capture_pageview: false since we handle SPA route changes manually.
 */
const posthogKey = import.meta.env.VITE_POSTHOG_KEY
const posthogHost = import.meta.env.VITE_POSTHOG_HOST

if (posthogKey && posthogHost) {
  posthog.init(posthogKey, {
    api_host: posthogHost,
    capture_pageview: false, // We capture manually for SPA routing
    capture_pageleave: true, // Capture when user leaves page
    persistence: 'localStorage',
    autocapture: true, // Autocapture clicks, form submissions, etc.
  })
} else if (import.meta.env.DEV) {
  console.warn('[PostHog] Missing VITE_POSTHOG_KEY or VITE_POSTHOG_HOST. Analytics disabled.')
}

/**
 * Phase 5F: Global focus guard
 *
 * Browsers often emit an initial focus event on page load.
 * With refetchOnWindowFocus:true, this causes immediate refetch after hydration.
 *
 * Solution: Ignore focus events for the first 2000ms after app boot.
 * After that, normal focus behavior resumes for SWR.
 */
const APP_BOOT_TIME = Date.now()
const BOOT_FOCUS_IGNORE_MS = 2000

focusManager.setEventListener((handleFocus) => {
  // Visibility change: pass whether document is visible (true/false)
  const onVisibilityChange = () => {
    if (Date.now() - APP_BOOT_TIME < BOOT_FOCUS_IGNORE_MS) {
      return
    }
    handleFocus(document.visibilityState === 'visible')
  }

  // Window focus: always means focused (true)
  const onFocus = () => {
    if (Date.now() - APP_BOOT_TIME < BOOT_FOCUS_IGNORE_MS) {
      return
    }
    handleFocus(true)
  }

  // Subscribe to focus events
  if (typeof window !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange, false)
    window.addEventListener('focus', onFocus, false)
  }

  // Return cleanup function
  return () => {
    if (typeof window !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', onFocus)
    }
  }
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
